import { Layer, ObjectCategory } from "@common/constants";
import { FireCellState, FireConfig } from "@common/definitions/fire";
import { type SkyName } from "@common/utils/mapWeather";
import { Container, RendererType, RenderTexture, Sprite, Texture, TilingSprite } from "pixi.js";
import { GameConsole } from "../console/gameConsole";
import { Game } from "../game";
import { type Player } from "../objects/player";
import { PIXI_SCALE } from "../utils/constants";
import { sampleNoise } from "../war/warNoise";
import { WarSky } from "../war/warSky";
import { CameraManager } from "./cameraManager";
import { FanMesh, LIGHT_HEIGHT, LightFans, RoofMaskedLights, roundUv, type UvMap } from "./lightFans";
import { cloudsAt, GLOW, HEAT, isLightingLevel, type LightingLevel, SKY_LIGHT, type Sun, sunAt } from "./lightingLogic";
import { NightLight, nightTextures } from "./nightLight";
import { NIGHT_ZINDEX } from "./nightLightLogic";

/*
    The lighting and the atmosphere (0.98.6, owner decision, 1 November 2026: "Add shadows + lightning and athmosphere
    + shaders ... Lighting and athmosphere also like shadows": Off, Low, High in Settings → Graphics, `cv_lighting`):

     - the sun (lightingLogic.ts `sunAt`): under each sky it says which way the shadows fall (managers/shadowLook.ts)
       and lights the ground's relief in the shaders (war/warShaders.ts). With the lighting on it moves over the match
       (the shadows turn, and lengthen as it sinks: at dusk from long to very long), and a thunderstorm's lightning
       throws hard black shadows away from each strike while it flashes
     - the clouds' shadows (Low and High): soft dark patches drifting over the ground with the wind, over the things on
       it too, fixed to the map as the camera moves: a tiling picture of noise, multiplied over the world under the
       sky's tint and the night's dark. None at night (the dark hides them); low, ragged and fast in a thunderstorm
     - the glows (Low and High): a warm light round a fire by day and at dusk, round the street lights at dusk, and a
       flash round a blast or a muzzle at any hour (at night these are the night's own: nightLight.ts erases the dark)
     - the bloom and the heat haze (High, with the shaders on: war/warBloom.ts): the bright things (a muzzle's flame,
       a tracer, a fire, a lamp, a blast, the lightning) glow past their edges, and the air wavers over a fire. The heat
       map (where the fires burn on screen) is drawn here each frame, a small texture the grade reads
     - the sun's rays at dusk (High): war/warSky.ts draws them with the sun's glow

    The night's dark and its lights are the game at every level (1.05: nightLight.ts); this is the look round them.

    0.98.7 (the owner: "Lights blocked by walls", "Lighting that shows each object's shape", "Damage that stays"): with
    the shadows on, the glows are fans cut by the walls (managers/lightFans.ts); with the lighting at High each adds
    its relief to the lighting's picture of the shapes (managers/normalLook.ts), the sun's own relief being the grade's
    (war/warShaders.ts); and `litTint` says how the light falls on a puff of smoke (managers/particleManager.ts), the
    sun's colour, a fire's warmth beside it, the night's dark.
*/

/** two tints multiplied (each channel) */
export function multiplyTint(a: number, b: number): number {
    const channel = (shift: number): number => Math.round((((a >> shift) & 0xff) * ((b >> shift) & 0xff)) / 255);
    return (channel(16) << 16) | (channel(8) << 8) | channel(0);
}

/** a moment of light: a blast's flash or a shot's, as a warm glow by day */
interface Flash {
    readonly x: number
    readonly y: number
    readonly layer: Layer
    readonly radius: number
    readonly start: number
    readonly duration: number
    readonly strength: number
}

/** a burning patch of a fire chunk (a 2 by 2 block of its cells): where (game units), how much of it burns (0..1) */
interface FireBlock {
    readonly x: number
    readonly y: number
    readonly burning: number
}

const MAX_FLASHES = 24;
const FIRE_BLOCK = 2;
/** the camera container's zIndex for the clouds' shadows (under the sky's tint and the night's dark) and the glows (over the clouds) */
export const CLOUD_ZINDEX = NIGHT_ZINDEX - 3;
export const GLOW_ZINDEX = NIGHT_ZINDEX - 2;
/** the clouds' picture: this many pixels across, one tile of the sky */
const CLOUD_SIZE = 256;

/** how bright a floor's things show now (its container's alpha, fading as you change floor), 0 if it isn't shown */
const shownOn = (layer: Layer): number => {
    const container = CameraManager.getContainer(layer);
    return container.visible ? container.alpha : 0;
};

/** the clouds' shadows: a tile of soft dark patches (black, their alpha the cover), from the look's own noise */
let cloudTexture: Texture | undefined;
const clouds = (): Texture => {
    if (cloudTexture && !cloudTexture.destroyed) return cloudTexture;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = CLOUD_SIZE;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        const image = ctx.createImageData(CLOUD_SIZE, CLOUD_SIZE);
        const data = image.data;
        const smooth = (a: number, b: number, x: number): number => {
            const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
            return t * t * (3 - 2 * t);
        };
        for (let y = 0; y < CLOUD_SIZE; y++) {
            for (let x = 0; x < CLOUD_SIZE; x++) {
                const u = (x + 0.5) / CLOUD_SIZE, v = (y + 0.5) / CLOUD_SIZE;
                // (big banks with ragged edges: the smooth noise, and a finer one torn into it; both tile)
                const n = 0.72 * sampleNoise(u, v, 0) + 0.28 * sampleNoise(u * 2 + 0.31, v * 2 + 0.67, 3);
                const cover = smooth(0.5, 0.8, n) * (0.7 + 0.3 * sampleNoise(u * 3 + 0.1, v * 3 + 0.4, 2));
                const i = (y * CLOUD_SIZE + x) * 4;
                data[i] = data[i + 1] = data[i + 2] = 0;
                data[i + 3] = Math.round(255 * cover);
            }
        }
        ctx.putImageData(image, 0, 0);
    }
    const texture = Texture.from(canvas);
    texture.source.addressMode = "repeat";
    return cloudTexture = texture;
};

class LightingClass {
    private _level: LightingLevel = "high";
    /** the lighting as the settings have it */
    get level(): LightingLevel { return this._level; }
    private _sky: SkyName = "clear";
    private _sun: Sun = SKY_LIGHT.clear;
    /** the sun as it is this frame: the shadows' way, the relief's light */
    get sun(): Sun { return this._sun; }
    private _started = 0;
    private _time = 0;
    private _playing = false;

    private _clouds?: TilingSprite;
    private _cloudDrift = { x: 0, y: 0 };
    private readonly _glow = new Container();
    /** (0.98.7) the glows by their floor, each kept off the roofs shown over it */
    private readonly _glowLights = new RoofMaskedLights(this._glow);
    private readonly _glowPool: Sprite[] = [];
    private _glowUsed = 0;
    private readonly _glowFans: FanMesh[] = [];
    /** where a glow's fan reads its picture, written for each fan as it's set */
    private readonly _uv: UvMap = { u0: 0, ux: 0, uy: 0, v0: 0, vx: 0, vy: 0 };
    private _glowFansUsed = 0;
    /** the glows drawn as fans this frame (the lab) */
    private _fanned = 0;
    get fanned(): number { return this._fanned; }
    private readonly _flashes: Flash[] = [];
    private _heatTarget?: RenderTexture;
    private readonly _heatScene = new Container();
    private readonly _heatPool: Sprite[] = [];
    private _heatUsed = 0;
    private _heatDrawn = false;
    private _blocks: FireBlock[] = [];

    private _listening = false;
    /** the wind the clouds go with (the war look's weather: WarLook.setupMap says) */
    wind = { x: 1, y: 0.3 };

    /** the setting, read as the match begins and whenever it's changed (it applies at once, mid-match too) */
    private _readLevel(): void {
        if (!this._listening) {
            this._listening = true;
            GameConsole.variables.addChangeListener("cv_lighting", () => this._readLevel());
        }
        const value = GameConsole.getBuiltInCVar("cv_lighting");
        this._level = isLightingLevel(value) ? value : "high";
    }

    /** the bloom and the heat haze (High, and only where the shaders draw the grade: war/warLook.ts asks) */
    get rich(): boolean { return this._level === "high"; }
    /** anything of it drawn (the clouds, the glows; the sun's drift) */
    get active(): boolean { return this._level !== "off"; }

    /** the map came, under this sky (before anything of the match is drawn) */
    begin(sky: SkyName): void {
        this._readLevel();
        this._sky = sky;
        this._started = performance.now();
        this._time = 0;
        this._playing = false;
        this._flashes.length = 0;
        this._sun = sunAt(sky, 0, this._level);
        this._cloudDrift = { x: 0, y: 0 };
    }

    /** leaving a match */
    reset(): void {
        this._sky = "clear";
        this._flashes.length = 0;
        this._playing = false;
        this._sun = SKY_LIGHT.clear;
        if (this._clouds) this._clouds.visible = false;
        this._glow.visible = false;
        this._heatDrawn = false;
        this._blocks = [];
    }

    /** a blast: a flash of warm light round it for a moment, by day (the night's is nightLight.ts's) */
    flash(position: { readonly x: number, readonly y: number }, layer: Layer, radius: number, duration: number, strength = 1): void {
        if (!this.active || NightLight.active) return;
        while (this._flashes.length >= MAX_FLASHES) this._flashes.shift();
        this._flashes.push({ x: position.x, y: position.y, layer, radius, start: Date.now(), duration, strength });
    }

    /** a shot: its muzzle's flash lights round the gun's end for a blink */
    muzzle(player: Player, length: number): void {
        if (!this.active || NightLight.active) return;
        const rotation = player.container.rotation;
        const px = player.container.position.x / PIXI_SCALE, py = player.container.position.y / PIXI_SCALE;
        // (0.98.7's audit: short of a wall in front of the gun, on the shooter's side of it)
        const reach = LightFans.muzzleReach(px, py, rotation, length, player.layer);
        this.flash({ x: px + Math.cos(rotation) * reach, y: py + Math.sin(rotation) * reach }, player.layer, 7, 90, 0.8);
    }

    /** the heat map this frame (the fires burning on screen, white where the air shimmers), or nothing when no fire burns on screen */
    get heat(): Texture | undefined { return this._heatDrawn ? this._heatTarget : undefined; }

    /** the burning patches of the fires on screen this frame (shared by the glows and the heat) */
    private _fireBlocks(): FireBlock[] {
        const blocks: FireBlock[] = [];
        const cells = FireConfig.chunkCells, cell = FireConfig.cellSize;
        const view = this._view();
        for (const chunk of Game.objects.getCategory(ObjectCategory.Fire)) {
            if (chunk.destroyed || chunk.burningCells === 0) continue;
            const x0 = chunk.chunkX * cells * cell, y0 = chunk.chunkY * cells * cell;
            if (x0 > view.maxX || y0 > view.maxY || x0 + cells * cell < view.minX || y0 + cells * cell < view.minY) continue;
            for (let by = 0; by < cells; by += FIRE_BLOCK) {
                for (let bx = 0; bx < cells; bx += FIRE_BLOCK) {
                    let burning = 0;
                    for (let dy = 0; dy < FIRE_BLOCK; dy++) {
                        for (let dx = 0; dx < FIRE_BLOCK; dx++) {
                            const state = chunk.cellState(bx + dx, by + dy);
                            if (state === FireCellState.Burning) burning += 1;
                            else if (state === FireCellState.Kindling) burning += 0.5;
                        }
                    }
                    if (burning === 0) continue;
                    blocks.push({ x: x0 + (bx + FIRE_BLOCK / 2) * cell, y: y0 + (by + FIRE_BLOCK / 2) * cell, burning: burning / (FIRE_BLOCK * FIRE_BLOCK) });
                }
            }
        }
        return blocks;
    }

    /** the screen's view of the world this frame (game units), a margin round it */
    private _view(margin = 20): { minX: number, minY: number, maxX: number, maxY: number } {
        const scale = CameraManager.container.scale.x;
        const offset = CameraManager.container.position;
        const { width, height } = Game.pixi.screen;
        const minX = -offset.x / scale / PIXI_SCALE, minY = -offset.y / scale / PIXI_SCALE;
        return { minX: minX - margin, minY: minY - margin, maxX: minX + width / scale / PIXI_SCALE + margin, maxY: minY + height / scale / PIXI_SCALE + margin };
    }

    private _glowSprite(texture: Texture): Sprite {
        let sprite = this._glowPool[this._glowUsed];
        if (!sprite) {
            sprite = new Sprite(texture);
            sprite.anchor.set(0.5);
            sprite.blendMode = "add";
            sprite.eventMode = "none";
            this._glowPool.push(sprite);
        }
        this._glowUsed++;
        sprite.visible = true;
        return sprite;
    }

    private _heatSprite(texture: Texture): Sprite {
        let sprite = this._heatPool[this._heatUsed];
        if (!sprite) {
            sprite = new Sprite(texture);
            sprite.anchor.set(0.5);
            sprite.eventMode = "none";
            this._heatPool.push(sprite);
            this._heatScene.addChild(sprite);
        }
        this._heatUsed++;
        sprite.visible = true;
        return sprite;
    }

    /** every frame, after the camera (the shadows and the shaders take the sun from here) */
    update(deltaMS: number): void {
        const playing = Game.gameStarted;
        if (playing && !this._playing) {
            // (the match's clock from its first frame on the ground: the planes' minute isn't the day's)
            this._playing = true;
            this._started = performance.now();
        }
        this._time = playing ? (performance.now() - this._started) / 1000 : 0;
        const lightning = this.active ? WarSky.lightning() : undefined;
        this._sun = sunAt(this._sky, this._time, this._level, lightning);
        this._blocks = [];
        this._heatDrawn = false;
        if (!playing || !this.active) {
            if (this._clouds) this._clouds.visible = false;
            this._glow.visible = false;
            return;
        }
        const dt = Math.min(deltaMS, 100) / 1000;
        this._updateClouds(dt);
        this._blocks = this._fireBlocks();
        if (this.rich) this._updateHeat();
    }

    /** every frame after the normals' picture and the fans' frame began (game.ts): the glows, as fans where the walls cut them */
    drawGlows(): void {
        if (!Game.gameStarted || !this.active) {
            this._glow.visible = false;
            return;
        }
        this._updateGlows();
    }

    /**
     * the light on something in the air at (x, y) (game units) on `layer`, as a tint over its own colour (0.98.7, the
     * smoke lit): the sun's colour by day, a fire's warmth beside it; at night a fire's or a flash's warmth alone (the
     * night's dark lies over the smoke already); white with the lighting off
     */
    litTint(x: number, y: number, layer: Layer): number {
        // (0.98.7's audit: the lighting off, nothing tinted; at night the dark's overlay darkens the smoke itself, and
        // lifts off it where a lamp, a beam or a fire erases the dark, so only a fire's or a flash's warmth is added)
        if (!this.active) return 0xffffff;
        const sun = this._sun;
        const night = NightLight.active;
        let r = night ? 1 : sun.light[0], g = night ? 1 : sun.light[1], b = night ? 1 : sun.light[2];
        // a fire's warmth: the nearest burning patch within its reach
        let warmth = 0;
        if (layer === Layer.Ground) {
            const reach = HEAT.reach * 1.2;
            for (const block of this._blocks) {
                const d = Math.hypot(block.x - x, block.y - y);
                if (d >= reach) continue;
                warmth = Math.max(warmth, (1 - d / reach) * (0.5 + 0.5 * block.burning));
            }
        }
        const now = Date.now();
        for (const flash of this._flashes) {
            if (flash.layer !== layer) continue;
            const age = (now - flash.start) / flash.duration;
            if (age >= 1) continue;
            const d = Math.hypot(flash.x - x, flash.y - y);
            if (d >= flash.radius) continue;
            warmth = Math.max(warmth, (1 - d / flash.radius) * flash.strength * (1 - age));
        }
        if (warmth > 0) {
            const k = Math.min(1, warmth) * (night ? 1 : 0.6);
            r = r + (1.25 - r) * k;
            g = g + (0.8 - g) * k;
            b = b + (0.45 - b) * k;
        }
        const channel = (v: number): number => Math.max(0, Math.min(255, Math.round(v * 255)));
        return (channel(r) << 16) | (channel(g) << 8) | channel(b);
    }

    /** a glow drawn as a fan cut by the walls, or round (`sprite`) when there's none; its relief added (High) */
    private _glowFan(key: string, x: number, y: number, layer: Layer, radius: number, tint: number, alpha: number, height: number, strength: number): void {
        const tex = nightTextures();
        const fan = LightFans.fan({ key, x, y, layer, radius, height });
        const px = x * PIXI_SCALE, py = y * PIXI_SCALE;
        // (on its floor's own container: kept off the roofs shown over it)
        const group = this._glowLights.group(layer);
        if (fan && fan.length >= 6) {
            let mesh = this._glowFans[this._glowFansUsed];
            if (!mesh) {
                mesh = new FanMesh(tex.glow, "add");
                this._glowFans.push(mesh);
            }
            this._glowFansUsed++;
            if (mesh.mesh.parent !== group) group.addChild(mesh.mesh);
            mesh.mesh.visible = true;
            mesh.set(px, py, fan, true, roundUv(px, py, radius * PIXI_SCALE, this._uv), PIXI_SCALE);
            mesh.mesh.tint = tint;
            mesh.mesh.alpha = alpha;
            this._fanned++;
        } else {
            const sprite = this._glowSprite(tex.glow);
            if (sprite.parent !== group) group.addChild(sprite);
            sprite.tint = tint;
            sprite.position.set(px, py);
            sprite.scale.set((radius * PIXI_SCALE) / (128 / 2));
            sprite.alpha = alpha;
        }
        LightFans.addRelief(x, y, layer, radius, height, strength * Math.min(1, alpha * 2), undefined, fan, true);
    }

    /** the clouds' shadows: a tile of them over the screen, fixed to the map, drifting with the wind */
    private _updateClouds(dt: number): void {
        const look = cloudsAt(this._sky, this._level);
        if (look.alpha <= 0) {
            if (this._clouds) this._clouds.visible = false;
            return;
        }
        const tile = this._clouds ??= (() => {
            const sprite = new TilingSprite({ texture: clouds(), width: 1, height: 1 });
            sprite.blendMode = "multiply";
            sprite.zIndex = CLOUD_ZINDEX;
            sprite.eventMode = "none";
            // (the clouds' shadows a touch blue, as the sky's light is)
            sprite.tint = 0xe8eef8;
            return sprite;
        })();
        if (tile.parent !== CameraManager.container) CameraManager.addObject(tile);
        const scale = CameraManager.container.scale.x;
        const offset = CameraManager.container.position;
        const { width, height } = Game.pixi.screen;
        const left = -offset.x / scale, top = -offset.y / scale;
        // (with the wind: the war look's rolls it with the weather; a cloud goes a few units a second)
        const wind = this.wind;
        const speed = 2.2 * look.speed * PIXI_SCALE;
        this._cloudDrift.x += wind.x * speed * dt;
        this._cloudDrift.y += wind.y * speed * dt;
        tile.visible = true;
        tile.position.set(left, top);
        tile.width = width / scale;
        tile.height = height / scale;
        const k = (look.size * PIXI_SCALE) / CLOUD_SIZE;
        tile.tileScale.set(k, k);
        // (the pattern's origin kept at the map's: the clouds stand still over the ground as the camera moves)
        tile.tilePosition.set(-left + this._cloudDrift.x, -top + this._cloudDrift.y);
        tile.alpha = look.alpha;
    }

    /** the glows by day and at dusk: the fires', the lamps' at dusk, the flashes' */
    private _updateGlows(): void {
        const glow = GLOW[this._sky];
        const now = Date.now();
        // (0.98.8: what was drawn last frame and isn't this frame is hidden after the glows, the rest left shown: hidden
        // and shown again every frame, the glows had Pixi build the world's whole draw list again every frame)
        const glowsBefore = this._glowUsed, fansBefore = this._glowFansUsed;
        this._glowUsed = 0;
        this._glowFansUsed = 0;
        this._fanned = 0;
        if (this._glow.parent !== CameraManager.container) {
            this._glow.zIndex = GLOW_ZINDEX;
            this._glow.eventMode = "none";
            CameraManager.addObject(this._glow);
        }

        // the fires: a warm light on each burning patch, flickering
        const fireShown = glow.fire > 0 ? shownOn(Layer.Ground) : 0;
        if (fireShown > 0.01) {
            for (const block of this._blocks) {
                const flicker = 0.82 + 0.18 * Math.sin(now * 0.017 + block.x * 0.7 + block.y * 1.3);
                this._glowFan(`fire:${block.x}:${block.y}`, block.x, block.y, Layer.Ground, HEAT.reach * 1.1, 0xffa24a, glow.fire * (0.5 + 0.5 * block.burning) * flicker * fireShown, LIGHT_HEIGHT.fire, 0.6);
            }
        }

        // (0.98.8: only the lights that reach the screen, as the night's lamps: every lamp and flash the page knows had
        // its fan made and drawn)
        const view = this._view(0);
        const reaches = (x: number, y: number, radius: number): boolean => x + radius >= view.minX && x - radius <= view.maxX && y + radius >= view.minY && y - radius <= view.maxY;

        // the street lights at dusk: a warm sodium pool round each lamp still standing
        if (glow.lamp > 0) {
            for (const lamp of NightLight.lamps()) {
                const shown = shownOn(lamp.layer);
                if (shown <= 0.01 || !reaches(lamp.x, lamp.y, lamp.radius * 0.85)) continue;
                this._glowFan(`lamp:${lamp.x.toFixed(1)}:${lamp.y.toFixed(1)}`, lamp.x, lamp.y, lamp.layer, lamp.radius * 0.85, 0xffbd6b, glow.lamp * shown, LIGHT_HEIGHT.lamp, 0.7);
            }
        }

        // the flashes: bright at once, fading out
        if (glow.flash > 0) {
            for (let i = this._flashes.length - 1; i >= 0; i--) {
                const flash = this._flashes[i];
                const age = (now - flash.start) / flash.duration;
                if (age >= 1) {
                    this._flashes.splice(i, 1);
                    continue;
                }
                const shown = shownOn(flash.layer);
                if (shown <= 0.01 || !reaches(flash.x, flash.y, flash.radius)) continue;
                this._glowFan(`flash:${flash.start}:${i}`, flash.x, flash.y, flash.layer, flash.radius, 0xffd9a0, glow.flash * flash.strength * (1 - age) * (1 - age) * shown, LIGHT_HEIGHT.flash, 1.1);
            }
        } else this._flashes.length = 0;
        for (let i = this._glowUsed; i < glowsBefore; i++) this._glowPool[i].visible = false;
        for (let i = this._glowFansUsed; i < fansBefore; i++) this._glowFans[i].mesh.visible = false;
        // (0.98.7: the roofs shown now masked out of each floor's glows; the glows are in the world's pixels)
        this._glowLights.update();
        this._glow.visible = this._glowUsed + this._glowFansUsed > 0;
    }

    /** the heat map: white where the air shimmers over the fires on screen (the grade wavers the picture there) */
    private _updateHeat(): void {
        const renderer = Game.pixi.renderer;
        if (renderer.type === RendererType.CANVAS) return;
        // (0.98.8: as the glows', the patches not drawn this frame hidden after, the rest left shown)
        const before = this._heatUsed;
        this._heatUsed = 0;
        if (this._blocks.length === 0 || shownOn(Layer.Ground) <= 0.01) {
            for (let i = 0; i < before; i++) this._heatPool[i].visible = false;
            return;
        }
        const screen = Game.pixi.screen;
        const width = Math.max(1, Math.ceil(screen.width * HEAT.scale)), height = Math.max(1, Math.ceil(screen.height * HEAT.scale));
        if (!this._heatTarget) {
            this._heatTarget = RenderTexture.create({ width, height, resolution: 1, scaleMode: "linear", dynamic: true });
        } else if (this._heatTarget.width !== width || this._heatTarget.height !== height) {
            this._heatTarget.resize(width, height);
        }
        const scale = CameraManager.container.scale.x;
        const offset = CameraManager.container.position;
        const k = scale * PIXI_SCALE * HEAT.scale;
        const tex = nightTextures();
        const now = Date.now();
        for (const block of this._blocks) {
            const sprite = this._heatSprite(tex.glow);
            sprite.position.set((offset.x + scale * block.x * PIXI_SCALE) * HEAT.scale, (offset.y + scale * block.y * PIXI_SCALE) * HEAT.scale);
            // (the shimmer rises: the patch's heat reaches further up the screen than down)
            const reach = (HEAT.reach * k) / (128 / 2);
            sprite.scale.set(reach, reach * 1.35);
            sprite.position.y -= HEAT.reach * k * 0.3;
            sprite.alpha = (0.55 + 0.45 * block.burning) * (0.9 + 0.1 * Math.sin(now * 0.011 + block.x));
        }
        for (let i = this._heatUsed; i < before; i++) this._heatPool[i].visible = false;
        renderer.render({ container: this._heatScene, target: this._heatTarget, clear: true, clearColor: [0, 0, 0, 0] });
        this._heatDrawn = true;
    }
}

export const Lighting = new LightingClass();
